import {
  type CustomFieldSchema,
  CustomFieldType,
  RelationTypeOptions,
  CustomFieldValidators,
} from '@tribeplatform/gql-client/types'

import { logger } from '../../common/lib/logger.js'
import {
  TITLE_FIELD_KEY,
  getFieldIcon,
  getFieldSetting,
} from '../../CustomField/utils.js'
import type { RawFilter, Filter } from '../types.js'

const DYNAMIC_FILTER_PREFIX = 'fields'

const canUseAsFilter = ({
  archived,
  type,
  key,
  typeOptions,
  items,
  readPrivacy,
}: CustomFieldSchema) => {
  const isRichText = type === CustomFieldType.richText
  const isTitle = key === TITLE_FIELD_KEY
  const isMedia =
    type === CustomFieldType.relation &&
    typeOptions?.relationType === RelationTypeOptions.Media
  const isMediaArray =
    type === CustomFieldType.array &&
    items?.type === CustomFieldType.relation &&
    items?.typeOptions?.relationType === RelationTypeOptions.Media
  const isPrivate = readPrivacy?.allow?.length > 0

  if (
    archived ||
    isRichText ||
    isTitle ||
    isMedia ||
    isMediaArray ||
    isPrivate
  ) {
    return false
  }

  return true
}

const getFieldSelectOptions = (
  field: CustomFieldSchema,
): Filter['extraProps']['selectOptions'] => {
  const selectOptions = getFieldSetting(
    field,
    'selectOptions',
  ) as Filter['extraProps']['selectOptions']

  if (selectOptions) {
    return selectOptions
  }

  // If select options is not defined, fall back to enumValidator
  try {
    const fieldValidators = field?.validators ?? field?.items?.validators
    if (!fieldValidators) {
      return []
    }

    const enumValidator = fieldValidators.find(
      validator => validator.validation === CustomFieldValidators.enum,
    )

    if (!enumValidator) {
      return []
    }

    const enumOptions = JSON.parse(enumValidator.value) as string[]
    const options = enumOptions.map<
      Filter['extraProps']['selectOptions'][number]
    >(option => ({ value: option, color: '' }))
    return options
  } catch (error) {
    logger.error('Could not parse enumValidator options')
    return []
  }
}

export const getDynamicFilters = (customFields: CustomFieldSchema[]) => {
  if (!customFields) {
    return []
  }

  const dynamicFilters: RawFilter[] = customFields
    .filter(canUseAsFilter)
    .map<RawFilter>((field, index) => {
      const { key, name, type, typeOptions } = field
      const subType = getFieldSetting(field, 'subtype') as Filter['subType']
      const selectOptions = getFieldSelectOptions(field)
      const memberGroup = getFieldSetting(field, 'inputProps')
        ?.memberGroup as Filter['extraProps']['memberGroup']
      const rawFilter: RawFilter = {
        id: `${DYNAMIC_FILTER_PREFIX}.${key}.${index}`,
        key: `${DYNAMIC_FILTER_PREFIX}.${key}`,
        name,
        type,
        subType,
        icon: getFieldIcon(field),
        extraProps: {
          selectOptions,
          memberGroup,
          typeOptions,
        },
      }

      return rawFilter
    })

  return dynamicFilters
}
